import { Controller } from "@hotwired/stimulus"

// ClickFunnels-style Video Facade Controller
// Uses Cloudflare Stream Player SDK to control an existing iframe.
//
// The SDK is loaded dynamically and provides a `Stream(iframe)` wrapper
// that exposes direct control: player.muted, player.currentTime, player.play()
// This works within user gesture context to unmute.
export default class extends Controller {
    static targets = ["iframe", "overlay"]

    connect() {
        this.isMuted = true
        this.player = null
        this.sdkLoaded = false

        // Load the Cloudflare Stream SDK
        this.loadSDK()
    }

    loadSDK() {
        // Check if already loaded
        if (window.Stream) {
            this.initPlayer()
            return
        }

        // Check if script is already being loaded
        if (document.querySelector('script[src*="embed.videodelivery.net/embed/sdk"]')) {
            // Wait for it to load
            this.waitForSDK()
            return
        }

        // Load the SDK script
        const script = document.createElement('script')
        script.src = 'https://embed.videodelivery.net/embed/sdk.latest.js'
        script.onload = () => {
            this.sdkLoaded = true
            this.initPlayer()
        }
        script.onerror = () => {
            console.warn('[VideoFacade] Failed to load Cloudflare Stream SDK, falling back to iframe swap')
        }
        document.head.appendChild(script)
    }

    waitForSDK() {
        let attempts = 0
        const check = () => {
            if (window.Stream) {
                this.sdkLoaded = true
                this.initPlayer()
            } else if (attempts < 20) {
                attempts++
                setTimeout(check, 250)
            }
        }
        check()
    }

    initPlayer() {
        if (!window.Stream || !this.hasIframeTarget) return

        try {
            this.player = window.Stream(this.iframeTarget)
            console.log('[VideoFacade] Player initialized via SDK')
        } catch (e) {
            console.warn('[VideoFacade] SDK init error:', e)
        }
    }

    enableSound(event) {
        if (event) {
            event.preventDefault()
            event.stopPropagation()
        }

        if (this.player) {
            // SDK approach: direct control within user gesture
            this.player.muted = false
            this.player.currentTime = 0
            this.player.controls = true
            this.player.play()
            console.log('[VideoFacade] Unmuted via SDK')
        } else if (this.hasIframeTarget) {
            // Fallback: postMessage to the iframe. Restrict targetOrigin to
            // the iframe's actual origin (only when YouTube) so a hijacked
            // / replaced iframe can't receive control commands.
            const iframe = this.iframeTarget
            const origin = this._safeIframeOrigin(iframe)
            try {
                iframe.contentWindow.postMessage(JSON.stringify({
                    event: 'command',
                    func: 'unmute'
                }), origin)
                iframe.contentWindow.postMessage(JSON.stringify({
                    event: 'command',
                    func: 'seekTo',
                    args: [0]
                }), origin)
            } catch (e) {
                console.warn('[VideoFacade] postMessage fallback failed:', e)
            }
        }

        // Hide the overlay
        if (this.hasOverlayTarget) {
            this.overlayTarget.classList.add('hidden')
        }

        this.isMuted = false

        // Request Fullscreen immediately after unmute (must be in the same user gesture)
        if (this.hasIframeTarget) {
            const iframe = this.iframeTarget
            try {
                if (iframe.requestFullscreen) {
                    iframe.requestFullscreen().catch(err => {
                        console.warn(`Error attempting to enable fullscreen: ${err.message}`)
                    })
                } else if (iframe.webkitRequestFullscreen) { /* Safari */
                    iframe.webkitRequestFullscreen()
                } else if (iframe.msRequestFullscreen) { /* IE11 */
                    iframe.msRequestFullscreen()
                }
            } catch (e) {
                console.warn("Fullscreen request failed", e)
            }
        }
    }

    // Resolves the safe targetOrigin for postMessage. Only YouTube origins
    // are returned verbatim; anything else falls back to the canonical
    // YouTube origin so we never send control commands to "*" (any frame).
    _safeIframeOrigin(iframe) {
        try {
            const u = new URL(iframe.src || "", window.location.href)
            if (/(^|\.)youtube(-nocookie)?\.com$/.test(u.hostname)) {
                return u.origin
            }
        } catch (_) { /* invalid URL */ }
        return "https://www.youtube.com"
    }
}
